import { useEffect, useState } from "react";

import { Classes, Colors } from "@blueprintjs/core";
import { useParams } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { AddToCollectionDialog } from "@/components/pages_logged_in/roleplays/AddToCollectionDialog";
import { EditScenario } from "@/components/pages_logged_in/roleplays/details/scenario/edit/EditScenario";
import { RoleplayListHeader } from "@/components/pages_logged_in/roleplays/RoleplayListHeader";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { AddButton } from "@/components/pieces/button/AddButton";
import { CloneScenarioDialog } from "@/components/pieces/roleplays/CloneScenarioDialog";
import { ScenarioCard } from "@/components/pieces/roleplays/ScenarioCard";
import { ScenarioDeleteConfirm } from "@/components/pieces/roleplays/ScenarioDeleteConfirm";
import { getUser } from "@/components/session/JwtTokenManager";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  PermissionLevel,
  QueryScenariosStatus,
  useRoleplayQuery,
  useScenariosListQueryLazyQuery,
  VisibilityScope,
} from "@/graphql";
import {
  ListScenario,
  ScenarioModal,
  ScenariosPageType,
  Scope,
} from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { useScenarioQueryParams } from "./hooks";

const PAGE_SIZE = 10;

interface ScenariosListProps {
  pageType?: ScenariosPageType;
  statusFilter: QueryScenariosStatus;
  scopeFilter?: Scope;
  title?: string;
  pageLoading?: boolean;
  showShortDesc?: boolean;
  showRank?: boolean;
  showDifficulty?: boolean;
  showRoleplay?: boolean;
  showScope?: boolean;
  showAction?: boolean;
  iconSize?: number;
  allowClickOnCard?: boolean;
  allowAdd?: boolean;
  showPlayButton?: boolean;
  onSelect?: (scenario: ListScenario) => void;
  pageSize?: number;
  selectedScenarioIds?: string[];
  loadingItem?: { loading: boolean; id?: string };
  showStatusFilter?: boolean;
  showPracticeStatusFilter?: boolean;
}

export function ScenariosList({
  pageType = "assigned",
  title = "",
  pageLoading = false,
  statusFilter,
  showShortDesc = false,
  showRank = true,
  showDifficulty = true,
  showRoleplay = true,
  showScope = false,
  showAction = false,
  iconSize = 100,
  allowClickOnCard = true,
  allowAdd = false,
  showPlayButton = true,
  onSelect,
  pageSize = PAGE_SIZE,
  selectedScenarioIds = [],
  loadingItem,
  showStatusFilter = false,
  showPracticeStatusFilter = false,
}: ScenariosListProps) {
  const [selectedScenario, setSelectedScenario] = useState<ListScenario | null>(
    null,
  );

  const {
    currentPage,
    setCurrentPage,
    selectedStatus,
    setSelectedStatus,
    selectedScopes,
    setSelectedScopes,
    selectedCollections,
    setSelectedCollections,
    selectedOwner,
    setSelectedOwner,
    selectedOrder,
    setSelectedOrder,
    selectedTags,
    setSelectedTags,
    query,
    setQuery,
    selectedHasPracticed,
    setSelectedHasPracticed,
  } = useScenarioQueryParams({
    statusFilter: showStatusFilter ? statusFilter : undefined,
  });

  const [currentActiveModal, setCurrentActiveModal] =
    useState<ScenarioModal | null>(null);

  // when useCurrentUser is used, it will cause a re-render of the component when re-visittab
  const currentUser = getUser();
  const [isViewAllCollections, setIsViewAllCollections] = useState(true);

  const roleplaySlug = useParams<{
    roleplaySlug?: string;
  }>()?.roleplaySlug;

  const { data: roleplayData } = useRoleplayQuery({
    variables: {
      slug: roleplaySlug,
    },
    skip: !roleplaySlug,
  });

  const roleplay = roleplayData?.roleplay;
  const [fetchScenarios, { data: scenariosData, loading }] =
    useScenariosListQueryLazyQuery({
      fetchPolicy: "no-cache",
    });

  const { data, totalRecords } = scenariosData?.scenarios || {};
  useEffect(() => {
    if (pageType === "personal") {
      setSelectedScopes([VisibilityScope.User]);
      setSelectedOwner(currentUser?.id ?? null);
    }
  }, [pageType, currentUser]);

  const handleFetchScenarios = () => {
    const permissionLevel =
      pageType === "all" ? PermissionLevel.View : undefined;
    const isOwner =
      pageType === "personal" ? true : selectedOwner !== null ? true : false;
    const isAssignee = pageType === "assigned";
    fetchScenarios({
      variables: {
        isAssignee,
        page: currentPage,
        pageSize,
        status: selectedStatus,
        scopes: selectedScopes ? selectedScopes : [],
        orderBy: selectedOrder,
        tagIds: selectedTags,
        collectionIds: isViewAllCollections
          ? null
          : selectedCollections.filter(
              // -1 is the default value for orphaned scenarios
              (collectionId) => collectionId !== "-1",
            ),
        isOwner,
        query: query,
        permissionLevel,
        includeOrphaned: isViewAllCollections
          ? null
          : selectedCollections.includes("-1"),
        hasPracticed: selectedHasPracticed,
      },
    });
  };

  useEffect(() => {
    handleFetchScenarios();
  }, [
    currentPage,
    selectedStatus,
    selectedScopes,
    query,
    selectedTags,
    selectedOwner,
    selectedCollections,
    selectedOrder,
    isViewAllCollections,
    selectedHasPracticed,
  ]);

  const loadingBody = (
    <Box>
      {Array.from({ length: 10 }, (_, index) => (
        <Box
          key={index}
          height={100}
          className={Classes.SKELETON}
          mt={index === 0 ? 0 : 24}
        />
      ))}
    </Box>
  );

  const emptyBody = (
    <Flex flexWrap="wrap" alignItems="center" justifyContent="center">
      <Box color={SUBTEXT_COLOR}>No Scenarios</Box>
    </Flex>
  );

  const scenarios = ensureNonNullArray(data);

  return (
    <Box>
      <RoleplayListHeader
        showStatusFilter={showStatusFilter}
        showPracticeStatusFilter={showPracticeStatusFilter}
        title={title}
        filterFor="scenario"
        selectedScopes={selectedScopes}
        selectedOwner={selectedOwner}
        selectedCollections={selectedCollections}
        selectedTags={selectedTags}
        onChangeScope={(scopes) => {
          setSelectedScopes(scopes);
          setCurrentPage(1);
        }}
        onSearch={(query) => {
          setQuery(query ? query.trim() : null);
          setCurrentPage(1);
        }}
        onOwnerChange={(owner) => {
          setSelectedOwner(owner);
          setCurrentPage(1);
        }}
        selectedStatus={selectedStatus}
        onStatusChange={(status) => {
          setSelectedStatus(status);
          setCurrentPage(1);
        }}
        hasPracticed={selectedHasPracticed}
        onHasPracticedChange={(hasPracticed) => {
          setSelectedHasPracticed(hasPracticed);
          setCurrentPage(1);
        }}
        loading={loading}
        allowAdd={allowAdd}
        onScenarioOrderChange={(order) => {
          setSelectedOrder(order);
          setCurrentPage(1);
        }}
        selectedScenarioOrder={selectedOrder}
        onTagsChange={(tagIds) => {
          setSelectedTags(tagIds);
          setCurrentPage(1);
        }}
        onCollectionsChange={(collectionIds) => {
          setSelectedCollections(collectionIds);
          setCurrentPage(1);
        }}
        onViewAllCollections={(isViewAll) => {
          setIsViewAllCollections(isViewAll);
        }}
        pageType={pageType}
      />
      {loading || pageLoading ? (
        loadingBody
      ) : (
        <Box mb={24}>
          {scenarios.length === 0 ? emptyBody : null}
          {scenarios.map((scenario, index) => {
            const isSelected = selectedScenarioIds.includes(scenario.id);
            const isLoading =
              loadingItem?.loading && loadingItem?.id === scenario.id;
            return (
              <Box mt={index > 0 ? 24 : 0} key={scenario.id}>
                <ScenarioCard
                  scenario={scenario}
                  roleplay={roleplay}
                  showShortDesc={showShortDesc}
                  showRank={showRank}
                  showDifficulty={showDifficulty}
                  showRoleplay={showRoleplay}
                  iconSize={iconSize}
                  allowClickOnCard={allowClickOnCard}
                  showPlayButton={showPlayButton && pageType !== "assigned"}
                  showScope={showScope}
                  showAction={showAction}
                  rightButton={
                    onSelect ? (
                      <AddButton
                        isAdded={isSelected}
                        isLoading={isLoading || false}
                        onAdd={() => onSelect(scenario)}
                        onRemove={() => onSelect(scenario)}
                      />
                    ) : null
                  }
                  onDelete={() => {
                    setCurrentActiveModal("deleteScenario");
                    setSelectedScenario(scenario);
                  }}
                  onCloneScenario={() => {
                    setCurrentActiveModal("cloneScenario");
                    setSelectedScenario(scenario);
                  }}
                  onAddToCollection={() => {
                    setCurrentActiveModal("addToCollection");
                    setSelectedScenario(scenario);
                  }}
                  onEditScenario={() => {
                    setCurrentActiveModal("editScenario");
                    setSelectedScenario(scenario);
                  }}
                  showAssignmentStatus={pageType === "assigned"}
                />
              </Box>
            );
          })}
        </Box>
      )}
      <Paging
        totalRecords={totalRecords ?? 0}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isSticky={true}
        style={{
          background: Colors.WHITE,
          padding: 10,
          marginTop: 20,
        }}
      />
      {selectedScenario && (
        <>
          <ScenarioDeleteConfirm
            open={currentActiveModal === "deleteScenario"}
            scenario={selectedScenario}
            onClose={() => setCurrentActiveModal(null)}
            onSuccess={() => {
              handleFetchScenarios();
              setCurrentActiveModal(null);
            }}
            shouldRedirectAfterDelete={false}
          />
          <CloneScenarioDialog
            open={currentActiveModal === "cloneScenario"}
            onClose={() => setCurrentActiveModal(null)}
            scenario={selectedScenario}
          />
          <AddToCollectionDialog
            open={currentActiveModal === "addToCollection"}
            onClose={() => setCurrentActiveModal(null)}
            scenario={selectedScenario}
          />
          <EditScenario
            key={selectedScenario?.id}
            initialTab={currentActiveModal === "editScenario" ? "basic" : null}
            onClose={(isSuccess?: boolean) => {
              setCurrentActiveModal(null);
              if (isSuccess) {
                handleFetchScenarios();
              }
            }}
            scenario={selectedScenario}
          />
        </>
      )}
    </Box>
  );
}
